import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarClock,
  Clock,
  Sparkles,
  ArrowRight,
  Info,
  KeyRound,
  Zap,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { currency, currencyFine } from "@/lib/format";
import {
  computePricing,
  hourlyRate,
  MEMBER_MIN_HOURS,
  NONMEMBER_MIN_DAYS,
  unitLabel,
} from "@/lib/pricing";

// Presets. Each entry is {key, label, hours, memberOnly}
// Note: Long-term Rental Term Leasing is now handled by a dedicated card below the planner,
// so RTL is NOT included as a preset here. Hourly is member-only.
const PRESETS = [
  { key: "h4", label: "4 hours", hours: 4, memberOnly: true, icon: Clock },
  { key: "d1", label: "1 day", hours: 24, memberOnly: true, icon: Clock },
  { key: "d3", label: "3 days", hours: 72, memberOnly: true, icon: Clock },
  { key: "wknd", label: "Weekend", hours: 72, memberOnly: true, icon: Sparkles },
  { key: "w1", label: "1 week", hours: 24 * 7, memberOnly: false, icon: CalendarClock },
  { key: "w2", label: "2 weeks", hours: 24 * 14, memberOnly: false, icon: CalendarClock },
  { key: "m1", label: "Monthly (30 days)", hours: 24 * 30, memberOnly: false, icon: CalendarClock },
];

function pad(n) { return String(n).padStart(2, "0"); }
function toLocalISO(d) {
  // yyyy-MM-ddTHH:mm suitable for <input type="datetime-local">
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function addHours(iso, hours) {
  const d = new Date(iso);
  d.setHours(d.getHours() + hours);
  return toLocalISO(d);
}

export const BookingPlanner = ({ vehicle, isMember, user }) => {
  const nav = useNavigate();

  const nowIso = useMemo(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 1); // start next hour
    return toLocalISO(d);
  }, []);

  const defaultEnd = useMemo(() => addHours(nowIso, isMember ? 24 : 24 * 7), [nowIso, isMember]);

  const [start, setStart] = useState(nowIso);
  const [end, setEnd] = useState(defaultEnd);
  const [activePreset, setActivePreset] = useState(isMember ? "d1" : "w1");

  const applyPreset = (preset) => {
    if (preset.memberOnly && !isMember) return;
    setStart(nowIso);
    setEnd(addHours(nowIso, preset.hours));
    setActivePreset(preset.key);
  };

  const pricing = useMemo(
    () => computePricing({ vehicle, startISO: start, endISO: end, isMember }),
    [vehicle, start, end, isMember]
  );

  useEffect(() => { setActivePreset(null); /* user changed dates manually */ /* eslint-disable-next-line */ }, [start, end]);

  const memHourly = hourlyRate(vehicle, true);

  const canContinue = pricing?.ok;

  const continueToBooking = () => {
    if (!user) {
      nav(`/login?next=/vehicles/${vehicle.id}/reserve`);
      return;
    }
    // Encode planner selection into query params so BookingFlowPage can prefill
    const q = new URLSearchParams();
    q.set("start", start);
    q.set("end", end);
    if (pricing?.mode === "hourly") q.set("plan", "hourly");
    else if (pricing?.member?.breakdown) q.set("plan", inferPlanKey(pricing.member.breakdown));
    nav(`/vehicles/${vehicle.id}/reserve?${q.toString()}`);
  };

  const activeBreakdown = (pricing?.used === "member" ? pricing.member?.breakdown : pricing?.standard?.breakdown) || [];

  return (
    <div className="space-y-4" data-testid="booking-planner">
      <div className="card-clean p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Plan your rental</div>
            <div className="font-display text-lg font-semibold">Live pricing</div>
          </div>
          {isMember ? (
            <span className="inline-flex items-center gap-1 badge-blue rounded-full px-3 py-1 text-[11px]">
              <Sparkles className="h-3 w-3" /> Member unlocked
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 badge-warning rounded-full px-3 py-1 text-[11px]">
              <KeyRound className="h-3 w-3" /> {NONMEMBER_MIN_DAYS}-day minimum
            </span>
          )}
        </div>

        {/* Preset chips */}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="planner-presets">
          {PRESETS.map((p) => {
            const isActive = activePreset === p.key;
            const disabled = p.memberOnly && !isMember;
            return (
              <button
                key={p.key}
                type="button"
                disabled={disabled}
                onClick={() => applyPreset(p)}
                className={`chip ${isActive ? "chip-active" : ""} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                data-testid={`preset-${p.key}`}
                title={disabled ? "Drive Alfa Club members only" : ""}
              >
                <p.icon className="h-3.5 w-3.5" />
                {p.label}
                {p.memberOnly && (
                  <Sparkles className="h-3 w-3 ml-0.5 text-[hsl(var(--primary))]" />
                )}
              </button>
            );
          })}
        </div>

        {/* Date/time pickers */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">Pick up</label>
            <input
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="mt-1 w-full h-11 rounded-lg border border-border bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
              data-testid="planner-start"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">Return</label>
            <input
              type="datetime-local"
              value={end}
              min={start}
              onChange={(e) => setEnd(e.target.value)}
              className="mt-1 w-full h-11 rounded-lg border border-border bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
              data-testid="planner-end"
            />
          </div>
        </div>

        {/* Duration summary */}
        <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground" data-testid="planner-duration">
          <Clock className="h-3.5 w-3.5 text-[hsl(var(--primary))]" />
          {pricing?.hours > 0 ? (
            pricing.mode === "hourly" ? (
              <span>{pricing.hours} hour{pricing.hours > 1 ? "s" : ""} · hourly billing (member benefit)</span>
            ) : (
              <span>{pricing.days} day{pricing.days > 1 ? "s" : ""} · {pricing.hours} hours total</span>
            )
          ) : (
            <span>Pick your dates to see live pricing.</span>
          )}
        </div>

        {/* Error / member-only nudge */}
        <AnimatePresence>
          {!pricing?.ok && pricing?.error && (
            <motion.div
              key="err"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.18 }}
              className={`mt-3 rounded-lg px-3 py-2 text-xs flex items-start gap-2 ${
                pricing?.memberOnly ? "badge-blue" : "badge-danger"
              }`}
              data-testid="planner-error"
            >
              <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>{pricing.error}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Live breakdown */}
        {pricing?.ok && (
          <div className="mt-5 rounded-xl border border-border p-4 bg-secondary/40" data-testid="planner-breakdown">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
              {pricing.mode === "hourly" ? "Hourly billing" : "Best price breakdown"}
            </div>
            <div className="mt-2 space-y-1.5">
              {activeBreakdown.map((b, i) => (
                <div key={i} className="flex items-center justify-between text-sm gap-3" data-testid={`planner-row-${b.unit}-${i}`}>
                  <div className="text-foreground min-w-0 flex-1">
                    {b.note ? (
                      <span className="text-foreground font-medium">{b.note}</span>
                    ) : (
                      <>
                        <span className="tabular-nums font-medium">{b.count}</span>{" "}
                        <span className="text-muted-foreground">{unitLabel(b.unit, b.count)}</span>
                      </>
                    )}
                    <span className="text-muted-foreground"> · </span>
                    <span className="tabular-nums text-muted-foreground">{currencyFine(b.rate)}</span>
                    {b.unit === "hour" && <span className="text-muted-foreground">/hr</span>}
                    {b.unit === "day" && <span className="text-muted-foreground">/day</span>}
                    {b.unit === "week" && <span className="text-muted-foreground">/week</span>}
                    {b.unit === "month" && <span className="text-muted-foreground">/month</span>}
                    {b.discount_pct > 0 && (
                      <span className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-[hsl(var(--blue-100))] text-[hsl(var(--primary))] px-1.5 py-0.5 text-[10px] font-medium" data-testid={`planner-discount-${i}`}>
                        −{b.discount_pct}%
                      </span>
                    )}
                  </div>
                  <div className="tabular-nums font-medium whitespace-nowrap">{currency(b.subtotal)}</div>
                </div>
              ))}
            </div>
            <Separator className="my-3" />
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{isMember ? "Member total" : "Total"}</div>
                <div className="font-display text-3xl font-semibold tabular-nums" data-testid="planner-total">
                  {currency(pricing.totalUsed)}
                </div>
              </div>
              {isMember && pricing.savings > 0 && (
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Standard</div>
                  <div className="text-sm line-through text-muted-foreground tabular-nums">{currency(pricing.standard?.total || 0)}</div>
                  <div className="mt-1 inline-flex items-center gap-1 text-[11px] badge-blue rounded-full px-2 py-0.5">
                    <Sparkles className="h-3 w-3" /> Save {currency(pricing.savings)}
                  </div>
                </div>
              )}
            </div>

            <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Refundable deposit {currency(vehicle.deposit)}</span>
              {!isMember && pricing.member?.total > 0 && (
                <span className="text-[hsl(var(--primary))] tabular-nums" data-testid="planner-member-hint-total">Members: {currency(pricing.member.total)}</span>
              )}
            </div>
          </div>
        )}

        <Button
          className="w-full mt-5 h-11 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]"
          onClick={continueToBooking}
          disabled={!canContinue}
          data-testid="vehicle-detail-continue-to-booking-button"
        >
          Continue to booking <ArrowRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Rental Term Leasing (long-term member plans) */}
      <RentalTermLeasingCard vehicle={vehicle} user={user} isMember={isMember} />
    </div>
  );
};

// -----------------------------------------------------------------------------
// Rental Term Leasing card — 3 / 6 / 12 month plans with per-term deposit.
// Deposit for a term = 13% × monthly rate × number of months (displayed as an
// absolute dollar figure; the 13% mechanic is not surfaced to the user).
// -----------------------------------------------------------------------------
const RTL_TERMS = [
  { key: "rtl_3", months: 3, label: "3 months" },
  { key: "rtl_6", months: 6, label: "6 months" },
  { key: "rtl_12", months: 12, label: "12 months" },
];

const RentalTermLeasingCard = ({ vehicle, user, isMember }) => {
  const nav = useNavigate();

  const terms = RTL_TERMS.map((t) => {
    const key = `price_rtl_${t.months}_monthly`;
    const monthly = Number(vehicle[key] || 0);
    if (!monthly) return { ...t, available: false };
    const deposit = Math.round(monthly * 0.13 * t.months);
    return { ...t, monthly, deposit, available: true };
  });

  const anyAvailable = terms.some((t) => t.available);
  if (!anyAvailable) return null;

  const startTerm = (t) => {
    // Prefill the booking flow with the correct plan_type key and a start = tomorrow
    const now = new Date();
    now.setHours(10, 0, 0, 0);
    now.setDate(now.getDate() + 1);
    const end = new Date(now);
    end.setMonth(end.getMonth() + t.months);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    const q = new URLSearchParams({ start: fmt(now), end: fmt(end), plan: t.key });
    nav(`/vehicles/${vehicle.id}/reserve?${q.toString()}`);
  };

  const ctaLabel = !user
    ? "Create account or sign in to access"
    : isMember
      ? null
      : "Become a Drive Alfa Club member to access";
  const ctaTo = !user ? `/login?next=/vehicles/${vehicle.id}` : "/drive-alfa-club";

  return (
    <div className="card-clean p-5" data-testid="rental-term-leasing-card">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Long-term plans</div>
          <div className="font-display text-lg font-semibold">Rental Term Leasing</div>
        </div>
        {isMember ? (
          <span className="inline-flex items-center gap-1 badge-blue rounded-full px-3 py-1 text-[11px]">
            <Sparkles className="h-3 w-3" /> Members only
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 badge-warning rounded-full px-3 py-1 text-[11px]">
            <KeyRound className="h-3 w-3" /> Members only
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3">
        {terms.map((t) => {
          const disabled = !t.available || !isMember;
          return (
            <div
              key={t.key}
              className={`rounded-xl border p-4 ${isMember && t.available ? "border-[hsl(var(--blue-400))] bg-[hsl(var(--blue-100))]/40" : "border-border bg-secondary/40 opacity-90"}`}
              data-testid={`rtl-card-${t.months}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{t.label}</div>
                  {t.available ? (
                    <div className="flex items-baseline gap-1 mt-0.5">
                      <span className="font-display text-2xl font-semibold tabular-nums" data-testid={`rtl-monthly-${t.months}`}>{currency(t.monthly)}</span>
                      <span className="text-xs text-muted-foreground">/month</span>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground mt-0.5">Coming soon</div>
                  )}
                </div>
                <div className="text-right">
                  {t.available && (
                    <>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Deposit</div>
                      <div className="text-sm font-medium tabular-nums" data-testid={`rtl-deposit-${t.months}`}>{currency(t.deposit)}</div>
                    </>
                  )}
                </div>
              </div>
              {isMember && t.available && (
                <Button
                  onClick={() => startTerm(t)}
                  className="w-full mt-3 rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]"
                  data-testid={`rtl-book-${t.months}`}
                >
                  Book {t.label} <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {ctaLabel && (
        <div className="mt-4">
          <Button asChild className="w-full rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--blue-500))]" data-testid="rtl-locked-cta">
            <Link to={ctaTo}>{ctaLabel}</Link>
          </Button>
          <p className="mt-2 text-[11px] text-muted-foreground text-center">
            {!user
              ? "Sign in required to book Rental Term Leasing plans."
              : "Drive Alfa Club membership unlocks these long-term plans."}
          </p>
        </div>
      )}
    </div>
  );
};

function inferPlanKey(breakdown) {
  const units = new Set((breakdown || []).map((b) => b.unit));
  if (units.has("month")) {
    const m = (breakdown.find((b) => b.unit === "month") || {}).count || 0;
    if (m >= 12) return "rtl_12";
    if (m >= 6) return "rtl_6";
    if (m >= 3) return "rtl_3";
    return "monthly";
  }
  if (units.has("week")) return "weekly";
  return "daily";
}
